import * as Sentry from "@sentry/react";
import React from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme/theme';
import { AuthProvider } from './context/AuthContext';
import { DashboardProvider } from './context/DashboardContext';
import { PostsProvider } from './context/PostsContext';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN || "https://16eacf6d2eccfb47fafa737b0edb0029@o4511182115504128.ingest.us.sentry.io/4511182155481088",
  sendDefaultPii: true,
  environment: process.env.REACT_APP_ENV || "production",
});

// Auto-inject X-Session-Token; detect 502/503/504 and network timeouts
const _origFetch = window.fetch.bind(window);
window.fetch = async function(url, opts = {}) {
  if (typeof url === "string" && url.includes("go.brandhives.com")) {
    const st = localStorage.getItem("nectar_st");
    if (st) {
      opts = { ...opts, headers: { ...(opts.headers || {}), "X-Session-Token": st } };
    }
  }
  try {
    const resp = await _origFetch(url, opts);
    if ([502, 503, 504].includes(resp.status) && typeof url === "string" && url.includes("go.brandhives.com")) {
      window.dispatchEvent(new CustomEvent("nectar:busy-bee", { detail: { url, status: resp.status } }));
      // Best-effort: log to backend (fire and forget)
      _origFetch(`${process.env.REACT_APP_API_URL || "https://go.brandhives.com"}/app/api/log-client-error`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          error_type: "busy_bee",
          endpoint: url,
          status_code: resp.status,
          error_message: `HTTP ${resp.status} from ${url}`,
          user_agent: navigator.userAgent,
          user_id: localStorage.getItem("nectar_uid") || null,
        }),
      }).catch(() => {});
    }
    return resp;
  } catch (err) {
    // Network timeout or connection refused
    if (typeof url === "string" && url.includes("go.brandhives.com")) {
      window.dispatchEvent(new CustomEvent("nectar:busy-bee", { detail: { url, status: 0, error: String(err) } }));
      _origFetch(`${process.env.REACT_APP_API_URL || "https://go.brandhives.com"}/app/api/log-client-error`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          error_type: "timeout",
          endpoint: url,
          status_code: 0,
          error_message: String(err),
          user_agent: navigator.userAgent,
          user_id: localStorage.getItem("nectar_uid") || null,
        }),
      }).catch(() => {});
    }
    throw err;
  }
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <AuthProvider>
      <DashboardProvider>
        <PostsProvider>
          <Sentry.ErrorBoundary fallback={<p>Something went wrong.</p>}>
            <App />
          </Sentry.ErrorBoundary>
        </PostsProvider>
      </DashboardProvider>
    </AuthProvider>
  </ThemeProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
